import type { ProviderKind } from "@cmmd-forge/contracts";
import type { RefObject } from "react";
import { SearchIcon } from "lucide-react";

import { NO_ZOOM_FIELD_TEXT_CLASS } from "../../lib/touchTarget";
import { ComboboxInput } from "../ui/combobox";

export function splitProviderModelKey(key: string): { provider: ProviderKind; slug: string } {
  const colon = key.indexOf(":");
  if (colon === -1) return { provider: key as ProviderKind, slug: "" };
  return {
    provider: key.slice(0, colon) as ProviderKind,
    slug: key.slice(colon + 1),
  };
}

export function ModelPickerSearchHeader(props: {
  /** The one sentence for the provider the rail has open, refusal or not. */
  readonly selectedProviderSentence: string | null;
  readonly searchInputRef: RefObject<HTMLInputElement | null>;
  readonly highlightedKeyRef: RefObject<string | null>;
  readonly searchQuery: string;
  readonly onSearchQueryChange: (query: string) => void;
  readonly onModelSelect: (provider: ProviderKind, slug: string) => void;
  readonly onRequestClose?: (() => void) | undefined;
}) {
  return (
    <div className="border-b px-3 py-2">
      {props.selectedProviderSentence ? (
        <p
          className="mb-2 text-sm leading-snug text-muted-foreground md:text-xs"
          data-testid="model-picker-provider-disabled-reason"
        >
          {props.selectedProviderSentence}
        </p>
      ) : null}
      <ComboboxInput
        ref={props.searchInputRef}
        className="min-h-touch rounded-md [&_input]:font-sans md:min-h-0"
        inputClassName={`${NO_ZOOM_FIELD_TEXT_CLASS} min-h-touch rounded-lg border-border bg-background px-2 shadow-none ring-0 transition-[border-color,box-shadow] placeholder:text-muted-foreground/55 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/35 md:h-9 md:min-h-0 [&_input]:h-touch [&_input]:min-h-touch [&_input]:leading-(--spacing-touch) md:[&_input]:h-9 md:[&_input]:min-h-0 md:[&_input]:leading-9`}
        placeholder="Search models..."
        showTrigger={false}
        startAddon={<SearchIcon className="size-4 shrink-0 text-muted-foreground/50" />}
        value={props.searchQuery}
        onChange={(event) => props.onSearchQueryChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            props.onRequestClose?.();
            return;
          }
          if (event.key === "Enter" && props.highlightedKeyRef.current) {
            const augmented = event as typeof event & {
              preventBaseUIHandler?: () => void;
            };
            augmented.preventBaseUIHandler?.();
            event.preventDefault();
            event.stopPropagation();
            const { provider, slug } = splitProviderModelKey(props.highlightedKeyRef.current);
            props.onModelSelect(provider, slug);
            return;
          }
          event.stopPropagation();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onTouchStart={(event) => event.stopPropagation()}
        size="sm"
      />
    </div>
  );
}
